import type { OrderItem } from "@/api/api-client"
import { useLanguage } from "@/hooks/useLanguage"
import {
  formatCurrency,
  formatPricePerWeightUnit,
  formatTotalWeight,
  formatUnitsBreakdown,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"

type OrderItemWithUnit = OrderItem & {
  weight?: number
  weightUnit?: string
  pricePerWeightUnit?: number
}

interface OrderItemsTableLabels {
  product: string
  sku: string
  quantity: string
  unitPrice: string
  total: string
  notAvailable: string
}

interface OrderItemsTableProps {
  items?: OrderItemWithUnit[]
  labels: OrderItemsTableLabels
}

export function OrderItemsTable({ items = [], labels }: OrderItemsTableProps) {
  const { t } = useLanguage()
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-border/50 bg-muted/5">
          <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {labels.product}
          </th>
          <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {labels.sku}
          </th>
          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {labels.quantity}
          </th>
          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {labels.unitPrice}
          </th>
          <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {labels.total}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border/50">
        {items.map((item) => {
          const qty = item.quantity ?? 0
          const weight = item.weight
          const weightUnit = item.weightUnit
          const hasWeight = weight !== undefined && weight !== null && weight > 0
          const perWeightUnitPrice = getPricePerWeightUnit(item)
          return (
            <tr key={item.id ?? item.productName} className="transition-colors hover:bg-muted/10">
              <td className="px-4 py-3 font-medium text-foreground">{item.productName}</td>
              <td className="px-4 py-3 text-muted-foreground">{item.sku || labels.notAvailable}</td>
              <td className="px-4 py-3 text-right">
                <div className="font-medium">
                  {formatUnitsBreakdown(qty, weight, weightUnit, t.units)}
                </div>
                {hasWeight && (
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {formatTotalWeight(qty, weight, weightUnit, t.units.total)}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-right text-muted-foreground">
                {hasWeight && perWeightUnitPrice > 0 ? (
                  <div className="flex flex-col items-end">
                    <span>
                      {formatPricePerWeightUnit(perWeightUnitPrice, weightUnit, {
                        withTax: !!item.isTaxable,
                        isTaxable: !!item.isTaxable,
                      })}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70">
                      {formatCurrency(item.price ?? 0)}/{t.units.perUnit}
                    </span>
                  </div>
                ) : (
                  <span>{formatCurrency(item.price ?? 0)}</span>
                )}
              </td>
              <td className="px-4 py-3 text-right font-medium text-foreground">
                {formatCurrency((item.price ?? 0) * qty)}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
